import React, { useState, Fragment, useRef, useMemo, useEffect, useLayoutEffect } from "react";

import {
  Breadcrumbs,
  ButtonPrimaryCompact,
  ButtonSecondaryCompact,
  ButtonTertiaryV2,
  colors2,
  DropdownSingle,
  LoaderOverlay,
  Modal,
  ModalConfirm,
  ModalSmall,
  SwitchSingle,
  TagDisplayV2,
  TextInput,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM_DF,
  UI_CAPTION,
  useBannerContext,
  usePopOutOfOverflowHiddenParent,
  Wrapper800,
  TooltipV2,
  UI_BODY_BOLD,
} from "oolib";
import { getSheetNames } from "./utils/getSheetNames";
import { SheetSelectView } from "./comps/SheetSelectView";
import { cloneDeep } from "lodash";
import { LAYOUT_CONSTANTS } from "@/_self_managed/LAYOUT_CONSTANTS";
import TableElem from "@/components/cards/TableElem";
import { genCleanCamelCaseId } from "@okf/ootils";
import { SM_PageHeader } from "../comps/SM_PageHeader";
import { SidebarLayoutV2 } from "@/components/layout/SidebarLayoutV2";
import { useDynamicLayoutHeight } from "@/components/analysisModules/InsightMatrix/utils/useDynamicLayoutHeight";
import { useNavigate, useParams } from "react-router-dom";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import {
  ButtonGhost,
  ButtonPrimary,
  ButtonSecondary,
} from "oolib/dist/v2/components/Buttons";
import { SystemComsTextBox } from "@/components/infoBoxes/SystemComsTextBox";
import { importInfoTooltipContent } from "./tooltipConfig";
import LexicalTextEditor from "@/components/inputs/TextEditor/LexicalTextEditor";
import { convertToLexicalText } from "@/utils/getters/gettersV2";
import { PopOutBadge } from "@/components/PopOutBadge";
import { useUploadDirectDataImportFileToGCS } from "./hooks/useUploadDirectDataImportFileToGCS";
import { useFetchFromExternalSource } from "./hooks/useFetchFromExternalSource";
import { useDirectImportPreviewRowsLazy } from "./hooks/useDirectImportPreviewRowsLazy";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import { usePendingDirectDataImportSession } from "./hooks/usePendingDirectDataImportSession";
import { buildSchemaConfigFromPendingDirectDataImport } from "./utils/buildSchemaConfigFromPendingDirectDataImport";
import { prepareSchemaExtractorForBackend } from "./utils/prepareSchemaExtractorForBackend";
import { useDirectImportData } from "./hooks/useDirectImportData";
import { useReimportWithTypeChanges } from "./hooks/useReimportWithTypeChanges";
import { useElemInView } from "@/utils/customHooks/useElemInView";
import { ImportModalShell } from "./comps/ImportModalShell";
import { GettingStartedModal } from "./comps/GettingStartedModal";
import { VerifyMappingModal } from "./comps/VerifyMappingModal";
import { SourceSelectionView } from "./comps/SourceSelectionView";
import {
  useExternalConnectionStatus,
  useLinkedIntegrationByContentType,
} from "@/utils/react-query-hooks/externalConnections";
import { getIntegrationIcon } from "./comps/IntegrationInterfaces";
import { slack } from "@/_self_managed/SM_Integrations/modals/Slack";
import { fathom } from "@/_self_managed/SM_Integrations/modals/Fathom";
import { intercom } from "@/_self_managed/SM_Integrations/modals/Intercom";
import { appStore } from "@/_self_managed/SM_Integrations/modals/AppStore";
import { playStore } from "@/_self_managed/SM_Integrations/modals/PlayStore";
import { AddDataView } from "./comps/AddDataView";
import { FieldTypeList } from "./comps/FieldTypeList";
import { Lightbox, LightboxTrigger, LightboxContent } from "@/components/UI/lightbox";
import { setVal } from "@okf/ootils";
import { DirectImport_AICat_ComsBox } from "@/components/infoBoxes/DirectImport_AICat_ComsBox";
import { SM_UIContent } from "../SM_UIContent";
import styled from "styled-components";
import { TableDirectImportFieldSelector } from "@/components/cards/TableElem/CONFIG_PARSER/TableCellBlocks";
import {
  StyledFieldSelectorButton,
  StyledConfidenceDot,
} from "@/components/cards/TableElem/CONFIG_PARSER/TableCellBlocks/TableDirectImportFieldSelector";
import { Caret } from "oolib/dist/v2/components/Dropdowns/comps/Caret";
import { genIcon } from "oolib/dist/v2/components/Dropdowns/utils/genIcon";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import { getTenant } from "@/TENANT_CONFIGS";
import {
  deriveFieldTypeKey,
  stripShortTextPresetProps,
} from "./textCompDisplay";
import ResizablePanel from "../SM_ResizablePanel";
import PublishedPreviewPageLayout from "./comp/PublishedPreviewPageLayout";
import { useDirectImportCompOptions } from "@/_self_managed/utils/useDirectImportCompOptions";
import { SWAPPABLE_FIELD_COMPS_SET } from "@/_self_managed/utils/swappableFieldComps";
import SavePendingDirectDataImportModal from "./comps/SavePendingDirectDataImportModal";


const HELPER__AUTO_INJECT_ROW_ID_KEY = "HELPER_autogen_row_id";

/**
 * Row of field-selector triggers, sitting above the table and sharing its column
 * widths.
 *
 * A trigger's contents don't shrink (see StyledTableDirectImportFieldSelector),
 * so a column narrower than the trigger needs clips the tail of it — the caret
 * goes first. This widens any such column to the trigger's natural width, making
 * that width the column's effective minimum: a narrower width (the 200px default,
 * or a drag past it) is corrected on the next render.
 *
 * Natural width doesn't depend on the column width — nothing in the trigger
 * flexes — so a correction never changes what the next measurement reads, and
 * this settles in one pass instead of oscillating.
 */
// oolib's Caret pads itself 1rem on the left, which sits on top of the row's own
// gap and makes the caret look further out than the pill and the dot. The gap
// owns the spacing here, so zero the padding out.
const StyledCaretWrapper = styled.div`
  display: flex;

  > * {
    padding-left: 0;
  }
`;

const FieldSelectorRow = ({ colWidthConfig, setColWidthConfig, children }) => {
  const rowRef = useRef(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const widened = {};
    row.querySelectorAll("[data-col-resize-target]").forEach((trigger) => {
      const colId = trigger.getAttribute("data-col-resize-target");
      const styles = getComputedStyle(trigger);
      const parts = Array.from(trigger.children);
      // The trigger is `justify-content: space-between`, so its min content
      // width is its parts laid end to end, plus the gaps and its own padding.
      const naturalWidth = Math.ceil(
        parts.reduce((sum, part) => sum + part.getBoundingClientRect().width, 0) +
          parseFloat(styles.paddingLeft) +
          parseFloat(styles.paddingRight) +
          (parseFloat(styles.columnGap) || 0) * Math.max(0, parts.length - 1)
      );

      if (naturalWidth > (parseInt(colWidthConfig?.[colId], 10) || 0)) {
        widened[colId] = `${naturalWidth}px`;
      }
    });

    if (Object.keys(widened).length) {
      setColWidthConfig((prev) => ({ ...prev, ...widened }));
    }
  });

  return (
    <div ref={rowRef} style={{ display: "flex" }}>
      {children}
    </div>
  );
};

const StyledTableDirectImportFieldSelector = styled(
  TableDirectImportFieldSelector
)`
  /* Column headers sit in a flex row alongside the table's own cells, so both
     the trigger and everything it holds (type icon, field name, NEW pill,
     confidence dot, caret) have to opt out of flex shrinking - otherwise a
     narrow column squeezes them instead of letting the header scroll with the
     rest of the table. */
  flex-shrink: 0;

  & .StyledFieldSelectorButton_styleOverrideHandle,
  & .StyledFieldSelectorButton_styleOverrideHandle * {
    flex-shrink: 0;
  }

  /* Closest the label side and the pill/caret side may ever come. The trigger is
     space-between, so this only bites at the column's minimum width - which
     FieldSelectorRow derives from this same gap, so the two stay in step. */
  & .StyledFieldSelectorButton_styleOverrideHandle {
    column-gap: 3rem;
  }

  &:hover {
    z-index: 1;
  }

  &:focus-within {
    z-index: 2;
  }

  & .StyledFieldSelectorButton_styleOverrideHandle::before {
    border-top-left-radius: ${({ isFirst }) => (isFirst ? "0.8rem" : 0)};
    border-top-right-radius: ${({ isLast }) => (isLast ? "0.8rem" : 0)};
    border-bottom-left-radius: ${({ isFirst }) => (isFirst ? "0.8rem" : 0)};
    border-bottom-right-radius: ${({ isLast }) => (isLast ? "0.8rem" : 0)};
    width: ${({ isLast }) => (isLast ? "100%" : "calc(100% + 1px)")};
  }
`;

// TagDisplay takes tagColor/textColor but hardcodes its radius and padding, and
// forwards neither className nor style — so the pill shape has to come from
// here. !important because what it hardcodes is an INLINE style, which a plain
// class rule can't outrank. Its StyledTag root is the only child.
const StyledNewPill = styled.div`
  display: flex;

  > * {
    border-radius: 100px !important;
    padding: 0.2rem 0.8rem !important;
  }
`;

const StyledConfirmModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  padding: 1.2rem 1.2rem 0;

`

const StyledConfirmModalActions = styled.div`
  display: flex;
  justify-content: flex-end; 
  gap: 0.6rem;
`

const fieldLayoutOptions = [
  {
    display: "New field every column",
    value: "newFieldEveryColumn",
    icon: "ArrowDown",
  },
  {
    display: "New field every row",
    value: "newFieldEveryRow",
    icon: "ArrowRight",
  },
];

/**
 * Function to sanitize column names by replacing dots with underscores
 */
const sanitizeColumnNames = (data) => {
  if (data.length === 0) return data;

  return [
    data[0].map((col) => col.replace(/\./g, "_")), // Only sanitize first row
    ...data.slice(1), // Keep rest unchanged
  ];
};

const TableWrapper = ({ children }) => {
  return (
    <div
      style={{
        padding: `0 ${LAYOUT_CONSTANTS.pagePadding}px`,
        height: "100vh",
        overflowY: "auto",
        paddingBottom: "15rem",
        scrollbarWidth: "none",
      }}
    >
      {children}
    </div>
  );
};


const validateHeaderCell = (field, newValue = null, outline = null) => {
  // Use newValue if provided, otherwise use field's label
  const labelValue =
    newValue !== null ? newValue : field.explicitBlockConfig?.label;

  // If explicitBlockConfig.label exists (even if blank or empty string), validate it
  if (
    labelValue !== undefined ||
    field.explicitBlockConfig?.label !== undefined
  ) {
    if (!labelValue || labelValue.trim() === "") {
      return "required";
    }

    // Check for invalid names (singular/plural variants)
    const invalidNames = ["tag", "tags", "theme", "themes", "subtheme", "subthemes"];
    // Normalize: trim, lowercase, remove special chars (spaces, hyphens, underscores, dots, etc.)
    const normalizedValue = labelValue
      .trim()
      .toLowerCase()
      .replace(/[\s\-_./\\]/g, "");

    if (invalidNames.includes(normalizedValue)) {
      return "invalidName";
    }

    // Check for duplicate names (only if outline is provided)
    if (outline) {
      const trimmedLabelLower = labelValue.trim().toLowerCase();
      const duplicateExists = outline.some((otherField) => {
        // Skip comparing with itself
        if (otherField.targetValuePath === field.targetValuePath) {
          return false;
        }

        // Get the label to compare
        const otherLabel =
          otherField.explicitBlockConfig?.label !== undefined
            ? otherField.explicitBlockConfig.label
            : otherField.sourceValuePath;

        return otherLabel && otherLabel.trim().toLowerCase() === trimmedLabelLower;
      });

      if (duplicateExists) {
        return "duplicate";
      }
    }
  }
  return null;
};

const validateAllHeaderCells = (outline) => {
  const errors = {};
  outline.forEach((field) => {
    const error = validateHeaderCell(field, null, outline);
    if (error) {
      errors[field.targetValuePath] = error;
    }
  });
  return errors;
};

/**
 * Reimport-only: validate the "map to existing field" choices.
 * - a column set to "Existing Field" must actually have a field selected
 * - two columns may not map onto the same existing field (also enforced by
 *   greying-out used options in the dropdown — this is the backstop)
 *
 * Returns an errors map keyed by targetValuePath (same shape as
 * validateAllHeaderCells) so the two merge cleanly into headerValidationErrors.
 */
const validateMappings = (outline = []) => {
  const errors = {};
  const usedTargets = {};
  // auto-matched same-name columns already occupy their existing target
  outline.forEach((field) => {
    if (field.isExistingTemplateField && field.targetValuePath) {
      usedTargets[field.targetValuePath] = true;
    }
  });
  outline.forEach((field) => {
    if (field.enabled === false) return;
    if (field.mappingMode !== "existing") return;
    const target = field.mappedField?.targetValuePath;
    if (!target) {
      errors[field.targetValuePath] = "mappingRequired";
      return;
    }
    if (usedTargets[target]) {
      errors[field.targetValuePath] = "duplicateMapping";
    } else {
      usedTargets[target] = true;
    }
  });
  return errors;
};

/**
 * Merge a field-selector's partial update `v` into the outline entry at `index`,
 * returning a new schemaConfig. Shared by the preview-table column selectors and
 * the Verify Mapping modal so a change in either edits the same column config
 * identically.
 *
 * `fieldTypeOptions` is the keyed option list (useDirectImportCompOptions) — a
 * field-type pick arrives as an option KEY, since Short Text and Long Text are
 * both `LexicalTextEditor` and differ only in their block-props preset.
 */
const applyOutlineFieldChange = (prev, index, v, fieldTypeOptions = []) => {
  const newConf = cloneDeep(prev);
  const currentOutline = newConf.schemaExtractor.outline[index];

  // Handle enabled toggle
  if (v.enabled !== undefined) {
    if (v.enabled === false) {
      currentOutline.enabled = false;
    } else {
      delete currentOutline.enabled;
    }
  }

  // Handle field-type change. Options are selected by KEY, not comp — Short
  // Text and Long Text share comp LexicalTextEditor and differ only in the
  // preset props.
  if (v.fieldTypeKey !== undefined) {
    const selectedKey = v.fieldTypeKey;
    if (selectedKey === "title") {
      currentOutline.comp = "TextInput";
      currentOutline.dummyCompNameValue = "title";
      // Title is a plain TextInput designation — a short-detected column must
      // not carry the Lexical preset into it.
      stripShortTextPresetProps(currentOutline);
    } else if (
      currentOutline._original &&
      selectedKey ===
        deriveFieldTypeKey(currentOutline._original, fieldTypeOptions)
    ) {
      // Existing field returned to its original type — restore the stored
      // identity EXACTLY (comp + explicitBlockConfig, incl. any annotation
      // opt-out). Covers: re-picking Short Text on a legacy TextInput field
      // (stays TextInput — never converted) and return-after-detour trips.
      currentOutline.comp = currentOutline._original.comp;
      if (currentOutline._original.explicitBlockConfig) {
        currentOutline.explicitBlockConfig = cloneDeep(
          currentOutline._original.explicitBlockConfig
        );
      } else {
        delete currentOutline.explicitBlockConfig;
      }
      delete currentOutline.dummyCompNameValue;
    } else {
      const selectedOption = fieldTypeOptions.find(
        (o) => o.key === selectedKey
      );
      if (selectedOption) {
        currentOutline.comp = selectedOption.comp;
        // Reset the text preset to the target type's default: strip the short
        // keys, then apply the option's preset (Short Text ships one; Long
        // Text none). AI state thereby resets to the new type's default by
        // construction.
        stripShortTextPresetProps(currentOutline);
        if (selectedOption.explicitBlockConfigPreset) {
          const preset = cloneDeep(selectedOption.explicitBlockConfigPreset);
          currentOutline.explicitBlockConfig = {
            ...(currentOutline.explicitBlockConfig || {}),
            ...preset,
            props: {
              ...(currentOutline.explicitBlockConfig?.props || {}),
              ...(preset.props || {}),
            },
          };
        }
        delete currentOutline.dummyCompNameValue;
      }
    }
  }

  // Handle tagType - store directly in outline
  // Use "in" check since undefined is a valid value to clear tagType
  if ("tagType" in v) {
    if (v.tagType) {
      currentOutline.tagType = v.tagType;
    } else {
      delete currentOutline.tagType;
    }
  }

  // Handle separation format
  if (v.separationFormat !== undefined) {
    if (v.separationFormat && v.separationFormat !== "none") {
      currentOutline.splitCharacterType = v.separationFormat;
    } else {
      delete currentOutline.splitCharacterType;
    }
  }

  // Handle separation format value (splitCharacter)
  if (v.separationFormatValue !== undefined) {
    if (v.separationFormatValue) {
      currentOutline.splitCharacter = v.separationFormatValue;
    } else {
      delete currentOutline.splitCharacter;
    }
  }

  // Handle date format selection (Date columns). Stored on the outline so
  // it's threaded to the parser at import — like timezone — instead of the
  // parser having to guess the format.
  if (v.detectedDateFormat !== undefined) {
    currentOutline.detectedDateFormat = v.detectedDateFormat;
  }

  // Confidence-dot acknowledgement: stamped once the user opens a
  // low-confidence column's picker (they've "looked at it"), which
  // permanently hides its dot. FE-only; stripped at submit.
  if (v.typeConfidenceAcked !== undefined) {
    currentOutline.typeConfidenceAcked = v.typeConfidenceAcked;
  }

  // "Include in AI Analysis" toggle — writes the registry kill-switch
  // (props.annotations.disable) directly onto the item's explicitBlockConfig.
  // Never touches the comp: Short and Long Text are both LexicalTextEditor;
  // Short merely defaults to OFF via the detection preset. The `shortText`
  // marker is preserved either way.
  if (v.includeInAiAnalysis !== undefined) {
    if (v.includeInAiAnalysis === false) {
      currentOutline.explicitBlockConfig = {
        ...(currentOutline.explicitBlockConfig || {}),
        props: {
          ...(currentOutline.explicitBlockConfig?.props || {}),
          annotations: {
            ...(currentOutline.explicitBlockConfig?.props?.annotations || {}),
            disable: true,
          },
        },
      };
    } else if (currentOutline.explicitBlockConfig?.props?.annotations) {
      const props = currentOutline.explicitBlockConfig.props;
      delete props.annotations.disable;
      if (Object.keys(props.annotations).length === 0) delete props.annotations;
      if (Object.keys(props).length === 0) {
        delete currentOutline.explicitBlockConfig.props;
        if (Object.keys(currentOutline.explicitBlockConfig).length === 0) {
          delete currentOutline.explicitBlockConfig;
        }
      }
    }
  }

  // Reimport mapping: "Existing Field" vs "New Field" toggle. We keep
  // the column's own auto-detected config intact and store the chosen
  // existing field as a `mappedField` overlay, applied at submit time
  // by prepareSchemaExtractorForBackend.
  if (v.mappingMode !== undefined) {
    if (v.mappingMode === "existing") {
      currentOutline.mappingMode = "existing";
    } else {
      currentOutline.mappingMode = "new";
      // switching back to a brand-new field clears any prior mapping
      delete currentOutline.mappedField;
    }
  }

  // User picked which existing field to assign this column to.
  if (v.mappedExistingField !== undefined) {
    currentOutline.mappingMode = "existing";
    currentOutline.mappedField = v.mappedExistingField; // { label, targetValuePath, comp, tagType? }
  }

  return newConf;
};

const PreviewLayout = ({
  schemaConfig,
  setSchemaConfig,
  ref_modalShell,
  headerValidationErrors,
  setHeaderValidationErrors,
  shouldValidateOnBlur,
  isModal,
  isImportMoreDataMode,
  onRowCountsChange,
}) => {
  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } =
    useGetQueryData("platformConfigs");

  // The import sheet, as the boundary a column's config panel is kept inside.
  // Without it the panel travels to the viewport edge with its column as the
  // table scrolls, leaving the sheet behind; with it, the panel stops at the
  // sheet's edge. Held in state rather than read off ref_modalShell.current so
  // floating-ui re-measures once the element exists (same rule as anchorEl).
  const [sheetEl, setSheetEl] = useState(null);
  useEffect(() => {
    setSheetEl(ref_modalShell?.current || null);
  }, [ref_modalShell]);

  // In-place field TYPE change on reimport is gated by FEATURES_DISABLED_ON_PROD
  // ('reimportFieldTypeChange'). When disabled, existing-field selectors stay
  // fully locked (pre-feature behavior). Mapping new columns onto existing
  // fields is a separate feature and is NOT gated here.
  const reimportTypeChangeDisabledOnProd = isFeatureDisabledOnProd(
    "reimportFieldTypeChange",
    {
      disabledFeatures,
      isProd: import.meta.env.VITE_ENV === "prod",
      tenant: getTenant(),
    }
  );
  // Keyed field-type options from the BE single source of truth (see the
  // hook — platformConfigs._DirectDataImport.fieldTypeOptions). The import
  // flow additionally offers the "Document Title" pseudo-option.
  const getFlatCompOptions = useDirectImportCompOptions({
    includeTitleOption: true,
  });

  const { gridHeightCSS, parentWrapperRef } = useDynamicLayoutHeight({
    offset: ref_modalShell?.current?.getBoundingClientRect()?.bottom
      ? window.innerHeight -
        ref_modalShell?.current?.getBoundingClientRect()?.bottom
      : 0,
  });
  const { SET_ALERT_BANNER } = useBannerContext();

  const seededTotal = schemaConfig?.metadata?.totalDataLength ?? 0;
  const seededRows = schemaConfig?.sampleData ?? [];

  const {
    data: previewRowsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDirectImportPreviewRowsLazy({
    fileNameInGcs: schemaConfig?.fileNameInGcs,
    autoInjectRowIdKey: HELPER__AUTO_INJECT_ROW_ID_KEY,
    sheetName: schemaConfig?.sheetName,
  });

  const { observerRef } = useElemInView({
    callback: !isFetchingNextPage && hasNextPage ? fetchNextPage : null,
  });

  // While the first batch is in flight (or if it hasn't fired yet),
  // show the rows already seeded into schemaConfig.sampleData so the
  // table never looks empty.
  const flatRows = useMemo(() => {
    const fetched = (previewRowsData?.pages || []).reduce(
      (acc, page) => [...acc, ...(page.rows || [])],
      []
    );
    return fetched.length > 0 ? fetched : seededRows;
  }, [previewRowsData, seededRows]);

  const totalDataLength =
    previewRowsData?.pages?.[0]?.totalDataLength ?? seededTotal;

  // The row count is displayed in the page header, which is rendered by the
  // parent — the lazy-paging hook that knows the counts lives in here, so they
  // are reported up as they change instead of the hook being lifted out.
  useEffect(() => {
    onRowCountsChange?.({ loaded: flatRows.length, total: totalDataLength });
  }, [flatRows.length, totalDataLength, onRowCountsChange]);

  /**
   * The row-count caption + field selectors are sticky at top 0, and the table
   * header sticks right below them. Measure instead of hardcoding an offset,
   * since the selector row's height moves with its content.
   */
  const stickyTopCompRef = useRef(null);
  const [stickyTopCompHeight, setStickyTopCompHeight] = useState(0);

  useEffect(() => {
    const elem = stickyTopCompRef.current;
    if (!elem) return;
    const observer = new ResizeObserver(() =>
      setStickyTopCompHeight(elem.getBoundingClientRect().height)
    );
    observer.observe(elem);
    return () => observer.disconnect();
  }, []);

  const renderTableDirectImportFieldSelectors = ({
    colWidthConfig,
    setColWidthConfig,
  }) => {
    // Create a map of tag types to field names for tracking which tags are already used
    const usedTagsMap = useMemo(() => {
      const map = {};
      schemaConfig?.schemaExtractor?.outline.forEach((field) => {
        // Use tagType field directly (stored in outline)
        if (field.tagType) {
          map[field.tagType] = field.sourceValuePath; // Store which field is using this tag type
        }
      });
      return map;
    }, [schemaConfig]);

    // Track which field is set as the title field
    const titleFieldInfo = useMemo(() => {
      const titleField = schemaConfig?.schemaExtractor?.outline.find(
        (field) => field.dummyCompNameValue === "title"
      );
      return titleField
        ? {
            fieldName: titleField.sourceValuePath,
            isUsed: true,
          }
        : { isUsed: false };
    }, [schemaConfig]);

    // targetValuePath -> claiming column's sourceValuePath. A target counts as
    // claimed when it's occupied by an auto-matched locked column
    // (isExistingTemplateField) or by another column the user mapped onto it.
    // Used to grey out taken options in the "Assign to existing field" dropdown
    // so two columns can't map to the same existing field. (Reimport only.)
    const usedExistingTargetsMap = useMemo(() => {
      const map = {};
      schemaConfig?.schemaExtractor?.outline.forEach((field) => {
        if (field.isExistingTemplateField && field.targetValuePath) {
          map[field.targetValuePath] = field.sourceValuePath;
        } else if (
          field.mappingMode === "existing" &&
          field.mappedField?.targetValuePath
        ) {
          map[field.mappedField.targetValuePath] = field.sourceValuePath;
        }
      });
      return map;
    }, [schemaConfig]);

    return schemaConfig?.schemaExtractor?.outline.map((option, i) => {
      const compOption = getFlatCompOptions.find(
        (d) => d.key === deriveFieldTypeKey(option, getFlatCompOptions)
      );
      const width = colWidthConfig?.[option.targetValuePath] || 300;
      const isFirst = i === 0;
      const isLast = i === schemaConfig?.schemaExtractor?.outline.length - 1;

      // On a subsequent import ("Import Data" into an existing dataset), fields
      // carried over from the existing template are flagged by the BE with
      // `isExistingTemplateField`. Their selectors USED to be fully locked (field
      // types could only be set on the first import). For self-managed tenants we
      // now allow editing the TYPE of an existing field on reimport: the changed
      // comp is applied BEFORE the import via changeFieldComp (which mints the
      // field's fresh _vN valuePath), then the outline is re-pointed onto the new
      // path and the import fires — see useReimportWithTypeChanges. This whole
      // component only renders for SM tenants (_self_managed/), so no extra tenant
      // gate is needed here.
      //
      // Existing fields are editable on reimport. On prod the whole type-change
      // capability is behind the 'reimportFieldTypeChange'
      // FEATURES_DISABLED_ON_PROD entry — when disabled, every existing field
      // stays locked.
      const isExistingTemplateField =
        isImportMoreDataMode && option.isExistingTemplateField === true;
      const isEditableExistingField =
        !reimportTypeChangeDisabledOnProd && isExistingTemplateField;
      const isLockedTemplateField =
        isExistingTemplateField && !isEditableExistingField;

      // On a reimport the SWAP TARGETS are limited to SWAPPABLE_FIELD_COMPS —
      // the same list the listing-header pencil offers — so a reimport can't
      // introduce Document Title / Date & Time / Simple Table / tag columns;
      // those stay available on a first import, which keeps the full
      // compsAvailableForDirectDataImport list.
      //
      // The column's OWN current type is always kept in the list, even when it
      // is one of the non-swappable ones (an existing Title / Date & Time / tag
      // field, which the BE hands back with its stored comp). That list is what
      // the selector resolves its current value against, so without this the
      // column has no option to display and renders as an empty selector. It's
      // marked disabled because it's the value, not a target: resolveFieldCompSwap
      // requires the STORED comp to be swappable too, so re-picking it is a no-op.
      //
      // Short↔Long on an existing Lexical field is a props-only change on the
      // same comp — the BE classifies it TYPE_UNCHANGED and silently drops it —
      // so the "other" text option is disabled with an explanation.
      const currentCompIsSwappable =
        !!compOption && SWAPPABLE_FIELD_COMPS_SET.has(compOption.comp);
      const fieldOptions = isImportMoreDataMode
        ? [
            ...(compOption && !currentCompIsSwappable
              ? [
                  {
                    ...compOption,
                    disabled: true,
                    desc: "This field type can't be changed on re-import",
                  },
                ]
              : []),
            ...getFlatCompOptions
              .filter((d) => SWAPPABLE_FIELD_COMPS_SET.has(d.comp))
              .map((d) => {
                if (
                  option._original?.comp === "LexicalTextEditor" &&
                  d.comp === "LexicalTextEditor" &&
                  d.key !==
                    deriveFieldTypeKey(option._original, getFlatCompOptions)
                ) {
                  return {
                    ...d,
                    disabled: true,
                    desc: "Short and Long Text can't be switched on an existing field",
                  };
                }
                return d;
              }),
          ]

        : getFlatCompOptions;

      return (
        <StyledTableDirectImportFieldSelector
          key={option.targetValuePath}
          value={option}
          disabled={isLockedTemplateField}
          icon={compOption?.icon}
          options={fieldOptions}
          usedTagsMap={usedTagsMap}
          titleFieldInfo={titleFieldInfo}
          // Reimport-only: lets a new column be mapped onto an existing field
          // instead of creating a new one ("Existing Field" vs "New Field").
          isImportMoreDataMode={isImportMoreDataMode}
          existingFields={schemaConfig?.existingFields || []}
          usedExistingTargetsMap={usedExistingTargetsMap}
          currentFieldName={
            option.explicitBlockConfig?.label !== undefined
              ? option.explicitBlockConfig.label
              : option.sourceValuePath
          }
          onChange={(k, v) => {
            // v contains partial updates - merge them directly into outline
            setSchemaConfig((prev) =>
              applyOutlineFieldChange(prev, i, v, getFlatCompOptions)
            );
          }}
          isFirst={isFirst}
          isLast={isLast}
          shiftBoundary={sheetEl}
        >
          {({
            open,
            effectiveIcon,
            selectedOption,
            enabled,
            showConfidenceDot,
            showNewPill,
          }) => (
            <StyledFieldSelectorButton
              className={"StyledFieldSelectorButton_styleOverrideHandle"}
              style={{
                width,
                ...(isLockedTemplateField && {
                  cursor: "not-allowed",
                  opacity: 0.6,
                }),
              }}
              data-col-resize-target={option.targetValuePath}
            >
              <div
                style={{
                  display: "flex",
                  gap: "0.8rem",
                  alignItems: "center",
                  zIndex: 1,
                  padding: "1rem 0",
                }}
              >
                {effectiveIcon &&
                  genIcon({
                    icon: option.enabled === false ? "X" : effectiveIcon,
                    color: colors2.grey80,
                    // Honour the field type's declared iconWeight (e.g. Single
                    // Select's filled RadioButton). "X" is the don't-import
                    // marker rather than a field-type icon, so it keeps
                    // genIcon's default weight.
                    weight:
                      option.enabled === false
                        ? undefined
                        : selectedOption?.iconWeight,
                  })}
                <UI_BODY_SM_DF>
                  {!enabled
                    ? SM_UIContent.SM_DirectDataImport.dropdowns.dontImportShort
                    : selectedOption?.display}
                </UI_BODY_SM_DF>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}
              >
                {showNewPill && (
                  /* The tooltip owns the wrapper: TagDisplay forwards only its
                     own props to the DOM, so `title` on it would be dropped. */
                  <TooltipV2
                    text="This column isn't in the dataset yet — importing will create a new field for it"
                    placement="bottom"
                    wrapperStyle={{ display: "flex" }}
                  >
                    <StyledNewPill>
                      <TagDisplayV2
                        display="NEW"
                        size="XS"
                        weight="bold"
                        tagColor="#7D5CFF4D"
                        textColor={colors2.primary}
                      />
                    </StyledNewPill>
                  </TooltipV2>
                )}
                {showConfidenceDot && (
                  <StyledConfidenceDot title="Auto-detected field type — check that it matches this column's data" />
                )}
                <StyledCaretWrapper>
                  <Caret showOptions={open} />
                </StyledCaretWrapper>
              </div>
            </StyledFieldSelectorButton>
          )}
        </StyledTableDirectImportFieldSelector>
      );
    });
  };

  const handleHeaderNameUpdate = (targetValuePath, newLabel) => {
    // Update config
    setSchemaConfig((prev) => {
      const newConf = cloneDeep(prev);
      const fieldIndex = newConf.schemaExtractor.outline.findIndex(
        (field) => field.targetValuePath === targetValuePath
      );

      if (fieldIndex !== -1) {
        if (!newConf.schemaExtractor.outline[fieldIndex].explicitBlockConfig) {
          newConf.schemaExtractor.outline[fieldIndex].explicitBlockConfig = {};
        }
        newConf.schemaExtractor.outline[fieldIndex].explicitBlockConfig.label =
          newLabel;
      }

      return newConf;
    });
  };

  const validateSingleHeader = (targetValuePath, newValue) => {
    // Only validate on blur if user has clicked "Confirm Import" at least once
    if (!shouldValidateOnBlur) return;

    const field = schemaConfig.schemaExtractor.outline.find(
      (f) => f.targetValuePath === targetValuePath
    );

    if (!field) return;

    const error = validateHeaderCell(
      field,
      newValue,
      schemaConfig.schemaExtractor.outline
    );

    setHeaderValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field.targetValuePath] = error;
      } else {
        delete newErrors[field.targetValuePath];
      }
      return newErrors;
    });
  };

  return (
    <div style={{ flex: "1 1 auto", paddingBottom: isModal ? "3.5rem" : "0" }}>
      <TableElem
        TableWrapper={TableWrapper}
        data={flatRows}
        lazyLoad={{
          observerRef: hasNextPage ? observerRef : null,
          isFetchingNextPage,
        }}
        headerEditable
        headerValidationErrors={headerValidationErrors}
        onHeaderNameUpdate={handleHeaderNameUpdate}
        validateSingleHeader={validateSingleHeader}
        enableContrastHeaderStyle
        customTopComp={({ colWidthConfig, setColWidthConfig }) => (
          <div
            ref={stickyTopCompRef}
            style={{
              paddingBottom: LAYOUT_CONSTANTS.topBottomPadding,
              paddingTop: LAYOUT_CONSTANTS.pagePadding,
              position: "sticky",
              top: 0,
              backgroundColor: colors2.white,
              zIndex: 1,
            }}
          >
            <FieldSelectorRow
              colWidthConfig={colWidthConfig}
              setColWidthConfig={setColWidthConfig}
            >
              {renderTableDirectImportFieldSelectors({
                colWidthConfig,
                setColWidthConfig,
              })}
            </FieldSelectorRow>
          </div>
        )}
        tableConfig={{
          rowIdPath: HELPER__AUTO_INJECT_ROW_ID_KEY,
          cellData: schemaConfig.schemaExtractor.outline.map((conf) => ({
            colId: conf.targetValuePath,
            headerDisplay:
              conf.explicitBlockConfig?.label !== undefined
                ? conf.explicitBlockConfig.label
                : conf.sourceValuePath,
            valuePath: conf.sourceValuePath,
            ...([
              "RadioList",
              "CheckboxList",
              "DropdownSingle",
              "DropdownMulti",
              "TagsInputSingle",
              "TagsInputMulti",
            ].includes(conf.comp)
              ? {
                  comp: "TableTags",
                  props: { tagsSplitCharacter: conf.splitCharacter },
                }
              : {}),
            // Date columns: only values that arrived as normalized UTC-ISO strings need
            // localizing (else 27 Jan IST, stored as 2026-01-26T18:30:00Z, reads a day
            // back). Those are exactly the columns detected as 'ISO' — i.e. genuine
            // Excel date cells, converted upstream. CSV/text dates are read as raw
            // strings on purpose (SheetJS can't be trusted to parse ambiguous DD/MM), so
            // they keep a human format like 'DD/MM/YYYY' and fall through to plain text,
            // rendering exactly as typed — nothing to localize, no day-back. ISO ones go
            // through the same Moment-based DateDisplay/DateTimeDisplay the content
            // tables use, which render the date in the viewer's timezone.
            ...(conf.detectedDateFormat === "ISO"
              ? conf.comp === "DatePicker"
                ? { comp: "DateDisplay" }
                : conf.comp === "DateTimePicker"
                ? { comp: "DateTimeDisplay" }
                : {}
              : {}),
            disabled: !(conf.enabled ?? true),
          })),
        }}
        withStickyHeader={true}
        stickyHeaderTopOffset={stickyTopCompHeight}
      />
    </div>
  );
};

// The integration these rows came from, sent with the import so the BE links
// the dataset (and seeds its schedule scope) on the first batch. Integrations
// tab launches carry it in the URL (`integrationActivation`); every other
// launch — Datasets tab, Import More Data, a resumed draft — uses the source
// the rows were actually fetched from (`schemaConfig.integrationProvider`).
// A CSV/Excel upload has neither, so it sends null.
const getImportIntegrationActivation = ({ integrationActivation, schemaConfig }) => {
  const provider = integrationActivation?.provider || schemaConfig?.integrationProvider;
  return provider
    ? { provider, adapterOptions: schemaConfig?.adapterOptions || {} }
    : null;
};

const ContentTypeTitleModal = ({
  schemaConfig,
  setSchemaConfig,
  directImportData,
  setShowContentTypeTitleModal,
  integrationActivation,
  isModal,
  onClose,
  pendingDirectDataImportId,
}) => {
  const [aiDescRich, setAiDescRich] = useState(
    schemaConfig?.tplConfigs?.aiDesc
      ? convertToLexicalText(schemaConfig?.tplConfigs?.aiDesc)
      : undefined
  );
  const [schemaConfig_tplConfigs, setSchemaConfig_tplConfigs] = useState(
    schemaConfig?.tplConfigs || {}
  );

  // Block creating a NEW dataset whose name collides with an existing one.
  // genCleanCamelCaseId(title) becomes the kp_content_type; if a tpl with that
  // content type already exists, the backend would silently APPEND to it (and
  // could mutate its schema) instead of creating a fresh dataset. Surface it
  // here and block import.
  const allTpls = useGetQueryData("allTpls") || [];
  const duplicateDatasetExists =
    !!schemaConfig_tplConfigs?.contentType &&
    allTpls.some(
      (t) => t.kp_content_type === schemaConfig_tplConfigs.contentType
    );

  return (
    // <ModalSmall title="Import New Dataset" onClose={()=> setShowContentTypeTitleModal(false)}>

    <div
      style={{
        // padding: LAYOUT_CONSTANTS.pagePadding + "px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <div>
        <TextInput
          forceFocus
          label={SM_UIContent.SM_DirectDataImport.display.nameOfDataset}
          value={schemaConfig_tplConfigs?.title}
          onChange={(k, v) => {
            setSchemaConfig_tplConfigs((prev) => ({
              ...prev,
              title: v,
              contentType: genCleanCamelCaseId(v),
            }));
          }}
        />
        {duplicateDatasetExists && (
          <UI_CAPTION color={colors2.redText}>
            A dataset with this name already exists. Please choose a different
            name.
          </UI_CAPTION>
        )}
      </div>
      <div>
        <div
          style={{
            display: "flex",
            marginBottom: "4px",
            justifyContent: 'space-between'
          }}
        >
          <PopOutBadge
            text={SM_UIContent.SM_DirectDataImport.display.contextForAI}
          />
          <TooltipV2
            text="Describe what this dataset contains and how it’s used so the AI can tailor highlights suggestion, and analysis accordingly."
            placement="bottom"
            trigger="click"
            style={{top:'2rem', left:'-1.5rem'}}>
            <ButtonGhost
              icon="Info"
              style={{ position: 'absolute', right: '1rem' }}
            />
          </TooltipV2>
        </div>
        <LexicalTextEditor
          styleAsTextInput
          label={SM_UIContent.SM_DirectDataImport.display.natureOFData}
          variant="simple"
          typo="UI_BODY_SM_DF"
          value={aiDescRich}
          onChange={(k, v) => {
            setAiDescRich(v);
            setSchemaConfig_tplConfigs((prev) => ({
              ...prev,
              aiDesc: v.allText,
            }));
          }}
          editorStyle={{ maxHeight: '17rem', overflowY: 'auto' }}
        />
      </div>
      <StyledConfirmModalActions>
        <ButtonGhost
          onClick={() => setShowContentTypeTitleModal(false)}>
          Cancel
        </ButtonGhost>

      <ButtonPrimary
        icon="Check"
        onClick={() => {
          directImportData(
            {
              config: {
                schemaExtractor: prepareSchemaExtractorForBackend(
                  schemaConfig.schemaExtractor
                ),
                tplConfigs: schemaConfig_tplConfigs,
              },
              fileNameInGcs: schemaConfig?.fileNameInGcs,
              sheetName: schemaConfig?.sheetName,
              // Which integration the rows came from (plus the adapter options
              // the user just used, so the BE can derive the cron scope from
              // this manual run). See getImportIntegrationActivation.
              integrationActivation: getImportIntegrationActivation({
                integrationActivation,
                schemaConfig,
              }),
              // Only an Integrations-tab launch returns the user to that page.
              redirectToIntegrations: !!integrationActivation,
              // Resumed-draft id → the BE deletes the draft on the first batch,
              // atomically within the tpl-creation transaction.
              pendingDirectDataImportId,
            },
            {
              onSettled: () => setSchemaConfig(undefined),
              onSuccess: () => {
                if (isModal && onClose) onClose();
              },
            }
          );

          setShowContentTypeTitleModal(false);
        }}
        disabled={
          !schemaConfig_tplConfigs?.title ||
          !schemaConfig_tplConfigs?.contentType ||
          duplicateDatasetExists
        }
      >
        {integrationActivation ? "Activate & Import" : "Begin Import"}
      </ButtonPrimary>
      </StyledConfirmModalActions>
    </div>

    // </ModalSmall>
  );
};

// Integrations whose connected import step is their Integrations popup.
const IMPORT_POPUPS = {
  slack,
  fathom,
  intercom,
  appStoreReviews: appStore,
  playStoreReviews: playStore,
};
// Integrations with nothing to connect: their popup always shows.
const NO_CONNECT_POPUPS = ["appStoreReviews", "playStoreReviews"];

export default ({ isModal, onClose, isImportMoreDataMode, skipPostImportNavigation = false, contentType: contentTypeFromProp, entryPoint, resumePendingDirectDataImport, fromManage, autoSubmitExternal }) => {
  const params = useParams();
  // Modal-mounted launches (e.g. SM_Integrations Sync now) need to inject
  // contentType via prop because /integrations route has no `:contentType`
  // segment in its path. Prop takes precedence; falls back to useParams
  // for the standard /datasets/:contentType/... routes.
  const contentTypeFromParams = contentTypeFromProp || params?.contentType;
  const tpl = useGetQueryData(["tpl", contentTypeFromParams]);
  // Which integration owns this dataset, if any. Drives the reimport source
  // list: no integration means it came from a file, so only offer the uploader.
  const linkedIntegration =
    useLinkedIntegrationByContentType()[contentTypeFromParams];
  const platformConfigs = useGetQueryData('platformConfigs');
  const shouldApplyOverflowHiddenFix = platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable

  // When the user lands here from the Integrations tab (Setup, or Add new
  // dataset), the URL carries `?integrationActivation=<provider>`. Captured once
  // on mount and threaded into the directImportData mutation so the BE worker can
  // create this dataset's integrationDatasetLink row on the first batch.
  const [integrationActivation] = useState(() => {
    const provider = new URLSearchParams(window.location.search).get("integrationActivation");
    return provider ? { provider } : null;
  });

  // Sheet selection. Only ever populated for a workbook with >1 sheet — a
  // single-sheet file (and every CSV) uploads straight through, so the common
  // case keeps its zero-friction drop-and-go. `pendingFile` holds the dropped
  // file hostage while the user answers.
  const [selectedSheetName, setSelectedSheetName] = useState(undefined);
  const [sheetNames, setSheetNames] = useState(undefined);
  const [pendingFile, setPendingFile] = useState(undefined);
  // Launched from a saved draft (Datasets page): seed schemaConfig from it so
  // the import-config screen shows on first render — an effect-based seed would
  // flash the data-source screen for a frame.
  const [schemaConfig, setSchemaConfig] = useState(() =>
    buildSchemaConfigFromPendingDirectDataImport(resumePendingDirectDataImport)
  );
  const [selectedFieldLayout, setSelectedFieldLayout] = useState(
    fieldLayoutOptions[0]
  );

  // Modal-launched imports should stay on the caller's page (e.g. /datasets,
  // /integrations) — the modal dismiss already returns the user to context, so
  // the global redirectAfterRegisterLink (which targets /dashboard) is wrong
  // for this flow.
  const { mutate: directImportData, isLoading: isImporting } =
    useDirectImportData({ skipPostImportNavigation: skipPostImportNavigation || isModal });

  // Reimport ("import more data") submit flow — see useReimportWithTypeChanges:
  // any existing-field type changes are PATCHed via changeFieldComp first, the
  // outline is re-pointed onto the freshly minted _vN valuePaths, and only then
  // does the import fire.
  const {
    reimportWithTypeChanges,
    isChangingFieldComp,
    isReImporting,
  } = useReimportWithTypeChanges({
    contentType: contentTypeFromParams,
    skipPostImportNavigation: skipPostImportNavigation || isModal,
  });

  const { mutate: uploadDirectDataImportFileToGCS, isLoading: isUploading } =
    useUploadDirectDataImportFileToGCS();
  const { mutate: fetchFromExternalSource, isLoading: isFetchingExternal } =
    useFetchFromExternalSource();

  // Track selected data source: null (show selector), "csvExcelUpload" (file
  // upload), or an adapter id (that integration's interface).
  // When launched from the Integrations page (Activate / Sync now / OAuth
  // return), the provider rides in on integrationActivation — open that
  // integration's interface directly instead of the source picker.
  const [selectedDataSource, setSelectedDataSource] = useState(
    () => integrationActivation?.provider || null
  );
  // A connected Slack, Fathom or Intercom gets its Integrations "Import from …"
  // popup in place of its interface step, wherever the wizard is opened. Not
  // connected (or stale), it keeps the interface, which owns the connect flow.
  const PopupModals = IMPORT_POPUPS[selectedDataSource];
  const { data: popupConn } = useExternalConnectionStatus(selectedDataSource, {
    enabled: !!PopupModals,
  });
  const showImportPopup =
    !!PopupModals &&
    (NO_CONNECT_POPUPS.includes(selectedDataSource) ||
      (!!popupConn?.connected && popupConn.status !== "stale"));

  // Inline error state passed down to integration interfaces so they can render
  // a contextual banner (e.g., "no Slack messages in this range") instead of
  // the global red error banner. Set on external-source fetch failure; cleared
  // when the user goes back to the source selector or retries.
  const [externalSubmitError, setExternalSubmitError] = useState(null);

  const [showContentTypeTitleModal, setShowContentTypeTitleModal] =
    useState(false);
  // "Verify Mapping" step — every Confirm Import goes through it. On a first
  // import it's followed by the name-the-dataset modal; on a reimport its
  // Continue fires the import directly. See VerifyMappingModal.
  const [showVerifyMappingModal, setShowVerifyMappingModal] = useState(false);
  // Field-type options for the Verify Mapping modal's per-column picker — same
  // list the preview table's column selectors use.
  const verifyMappingCompOptions = useDirectImportCompOptions({
    includeTitleOption: true,
  });
  // Reported up by PreviewLayout so the page header can show the row count.
  const [previewRowCounts, setPreviewRowCounts] = useState(null);
  const [headerValidationErrors, setHeaderValidationErrors] = useState({});
  const [shouldValidateOnBlur, setShouldValidateOnBlur] = useState(false);
  // "Getting Started" overlay shown on top of the preview/table view every time
  // a fresh preview loads (set true alongside each new schemaConfig below).
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const { SET_ALERT_BANNER } = useBannerContext();

  // Reset the import session's local UI state and close — used after a draft is
  // saved (the user returns to the Datasets page, where the draft now shows).
  const resetImportSession = () => {
    setSchemaConfig(undefined);
    setHeaderValidationErrors({});
    setShouldValidateOnBlur(false);
    if (isModal && onClose) onClose();
  };

  // "Save import for later" lifecycle: save/re-save a draft, clean it up once
  // the import goes through, delete it on discard. See usePendingDirectDataImportSession.
  const pendingDirectDataImportSession = usePendingDirectDataImportSession({
    resumePendingDirectDataImport,
    onSessionExit: resetImportSession,
  });

  // Reimport submit — any existing-field type changes are applied (via
  // changeFieldComp) BEFORE the import fires, see useReimportWithTypeChanges.
  // Called from Verify Mapping's Continue, which is the last step of both paths.
  const submitReimport = () => {
    reimportWithTypeChanges({
      schemaConfig,
      integrationActivation: getImportIntegrationActivation({
        integrationActivation,
        schemaConfig,
      }),
      redirectToIntegrations: !!integrationActivation,
      onImportSettled: () => setSchemaConfig(undefined),
      onImportSuccess: () => {
        if (isModal && onClose) onClose();
      },
    });
  };

  const ref_modalShell = useRef(null);
  const discardImportMsg = "Cancel import? All import settings you've configured will be discarded.";

  const navigate = useNavigate();
  const navigationGuard = useNavigationGuard({
    shouldGuard: !!schemaConfig,
    message: discardImportMsg,
    navigate,
    onClose,
  });

  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // CSV/Excel upload: push the file to GCS, then seed schemaConfig from the
  // generated outline (which moves the flow on to the preview/config step).
  // `sheetName` is undefined for CSV and single-sheet files; the BE then falls
  // back to the first sheet, which is exactly the old behaviour.
  const startUpload = (file, sheetName) => {
    uploadDirectDataImportFileToGCS(
      {
        file,
        sheetName,
        autoInjectRowIdKey: HELPER__AUTO_INJECT_ROW_ID_KEY,
        // Pass contentType for reimport mode to get the stored outline.
        ...(isImportMoreDataMode &&
          contentTypeFromParams && { contentType: contentTypeFromParams }),
      },
      {
        onSuccess: (data) => {
          // Drop the helper-row config so it doesn't show up as a column.
          // For fields that already exist in the template (reimport), snapshot
          // their original identity (_original, FE-only, stripped at submit)
          // so the onChange reducer can restore comp + explicitBlockConfig
          // exactly when the user returns to the field's original type.
          const _outline = data.config
            .filter((d) => d.sourceValuePath !== HELPER__AUTO_INJECT_ROW_ID_KEY)
            .map((d) =>
              d.isExistingTemplateField
                ? {
                    ...d,
                    _original: {
                      comp: d.comp,
                      explicitBlockConfig: cloneDeep(d.explicitBlockConfig),
                    },
                  }
                : d
            );

          setSchemaConfig({
            tplConfigs: { segment: "publishing" },
            schemaExtractor: { outline: _outline },
            existingFields: data.existingFields || [],
            sampleData: data.sampleData,
            fileNameInGcs: data.fileNameInGcs,
            // Carried alongside fileNameInGcs because every later call that
            // re-reads the file (lazy row paging, the queued import) has to
            // name the same sheet — otherwise we'd preview one sheet and
            // import another.
            sheetName,
            metadata: data.metadata,
          });
          setShowGettingStarted(true);
        },
        onError: () => {
          SET_ALERT_BANNER(
            SM_UIContent.SM_DirectDataImport.bannerAlert.error1,
            "red",
            4000
          );
        },
      }
    );
  };

  // Ask which sheet ONLY when the workbook actually has a choice to make.
  // Reading the names is a cheap index-only read (no cell data), so it costs
  // nothing on the common single-sheet path.
  const handleFileSelect = async (file) => {
    const isCsv = file.type === "text/csv" || /\.csv$/i.test(file.name);
    if (isCsv) return startUpload(file, undefined); // CSV has no sheets

    let names;
    try {
      names = await getSheetNames(file);
    } catch {
      // Couldn't read the index (corrupt/odd file). Don't block the import —
      // hand it to the BE, which defaults to the first sheet and surfaces a
      // real parse error if the file is genuinely broken.
      return startUpload(file, undefined);
    }

    if (names.length <= 1) return startUpload(file, names[0]);

    setPendingFile(file);
    setSheetNames(names);
    setSelectedSheetName(names[0]);
  };

  const clearSheetSelection = () => {
    setPendingFile(undefined);
    setSheetNames(undefined);
    setSelectedSheetName(undefined);
  };

  const handleSheetSelectConfirm = () => {
    startUpload(pendingFile, selectedSheetName);
    clearSheetSelection();
  };

  const isAwaitingSheetChoice = !!pendingFile && sheetNames?.length > 1;

  // External adapters (Slack, Play Store, etc.): fetch + generate the outline,
  // then seed schemaConfig the same way the upload path does.
  const handleExternalSourceSubmit = ({
    sourceId,
    apiKey,
    adapterOptions = {},
  }) => {
    setExternalSubmitError(null);
    fetchFromExternalSource(
      {
        sourceId,
        apiKey,
        adapterOptions,
        autoInjectRowIdKey: HELPER__AUTO_INJECT_ROW_ID_KEY,
        // Same as the upload path: reimport sends the dataset, so the BE matches
        // columns against its fields and can reject a form that doesn't match
        // the one this dataset is pinned to.
        ...(isImportMoreDataMode &&
          contentTypeFromParams && { contentType: contentTypeFromParams }),
      },
      {
        onSuccess: (data) => {
          // Same _original stamping as the upload path (see handleFileSelect).
          const _outline = data.config
            .filter((d) => d.sourceValuePath !== HELPER__AUTO_INJECT_ROW_ID_KEY)
            .map((d) =>
              d.isExistingTemplateField
                ? {
                    ...d,
                    _original: {
                      comp: d.comp,
                      explicitBlockConfig: cloneDeep(d.explicitBlockConfig),
                    },
                  }
                : d
            );

          // Surface per-channel failures (multi-channel Slack, etc.) as a
          // warning — the import did succeed for the other channels.
          const adapterFailures = data?.metadata?.adapterFailures || [];
          if (adapterFailures.length > 0) {
            const summary = adapterFailures
              .map((f) => `#${f.channelName || f.channelId} (${f.error})`)
              .join(", ");
            SET_ALERT_BANNER(
              `Imported successfully, but ${adapterFailures.length} channel${adapterFailures.length > 1 ? "s" : ""} failed: ${summary}. You can re-import them later.`,
              "yellow",
              8000
            );
          }

          setSchemaConfig({
            tplConfigs: { segment: "publishing" },
            schemaExtractor: { outline: _outline },
            existingFields: data.existingFields || [],
            sampleData: data.sampleData,
            fileNameInGcs: data.fileNameInGcs,
            metadata: data.metadata,
            // Snapshot the adapter options that produced this import so the BE
            // can derive cronConfig.scope from the latest manual sync.
            adapterOptions,
            // Which integration fetched these rows, so the import links the
            // dataset whichever screen it was started from. A later CSV upload
            // replaces schemaConfig wholesale, so this never leaks into it.
            integrationProvider: sourceId,
          });
          setShowGettingStarted(true);
        },
        onError: (error) => {
          // BE returns { errors: [{ msg }] } via handleErrorResponse; fall back
          // through the other shapes so specific messages aren't masked.
          const message =
            error?.response?.data?.errors?.[0]?.msg ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to fetch data from external source";
          // Hand the error to the interface for an inline banner. Skip the
          // global red banner for adapters that own their own error UI.
          setExternalSubmitError({ sourceId, message });
          const adapterOwnsErrorUI =
            sourceId === "slack" ||
            sourceId === "intercom" ||
            NO_CONNECT_POPUPS.includes(sourceId);
          if (!adapterOwnsErrorUI) {
            SET_ALERT_BANNER(message, "red", 4000);
          }
        },
      }
    );
  };

  // Picks made elsewhere (autoSubmitExternal): fetch them on open, unless the
  // integration's popup runs its own fetch (it gets them as initialOptions).
  useEffect(() => {
    if (
      autoSubmitExternal &&
      !IMPORT_POPUPS[autoSubmitExternal.sourceId]?.runsOwnFetch
    )
      handleExternalSourceSubmit(autoSubmitExternal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The compact source-selection + add-data (+ sheet-select) steps (no
  // schemaConfig yet). The same content is used two ways: modal launches wrap
  // it in ImportModalShell (oolib Modal); full-page routes render it inline in
  // the page below (no modal). Once data is loaded (schemaConfig set) both fall
  // through to the full-size preview/config view.
  //
  // Sheet-select sits between add-data and upload, and only for a workbook with
  // more than one sheet — it's a view swap like the others (NOT a modal on top),
  // so it inherits the shell and never fights it for stacking order.
  //
  // It MUST be checked before `!selectedDataSource`: the CSV/Excel dropzone
  // lives inside SourceSelectionView, so a file is dropped while
  // selectedDataSource is still null. If `!selectedDataSource` won, we'd fall
  // back to the source grid and the sheet step would be unreachable.
  const compactImportContent = isAwaitingSheetChoice ? (
    <SheetSelectView
      sheetNames={sheetNames}
      selectedSheetName={selectedSheetName}
      setSelectedSheetName={setSelectedSheetName}
      onConfirm={handleSheetSelectConfirm}
      onGoBack={clearSheetSelection}
    />
  ) : !selectedDataSource ? (
    <SourceSelectionView
      onSelectSource={setSelectedDataSource}
      onFileSelect={handleFileSelect}
      isUploading={isUploading}
      hideExternalSources={isImportMoreDataMode && !linkedIntegration}
    />
  ) : showImportPopup ? (
    <PopupModals.ImportModal
      Icon={getIntegrationIcon(selectedDataSource)}
      // Inside DirectDataImportModal, whose wrapper already tints the page.
      hideBackdrop={isModal}
      onClose={isModal ? () => onClose && onClose() : () => setSelectedDataSource(null)}
      // Back to the source picker, unless launched straight into the
      // integration from the Integrations page (no picker to go back to).
      onBack={integrationActivation ? undefined : () => setSelectedDataSource(null)}
      onContinue={(adapterOptions) =>
        handleExternalSourceSubmit({ sourceId: selectedDataSource, adapterOptions })
      }
      fromManage={fromManage}
      initialOptions={autoSubmitExternal?.adapterOptions}
      isLoading={isFetchingExternal}
      error={
        externalSubmitError?.sourceId === selectedDataSource
          ? externalSubmitError.message
          : null
      }
    />
  ) : (
    <AddDataView
      selectedDataSource={selectedDataSource}
      onFileSelect={handleFileSelect}
      onExternalSourceSubmit={handleExternalSourceSubmit}
      isUploading={isUploading}
      isFetchingExternal={isFetchingExternal}
      externalSubmitError={externalSubmitError}
      onClearSubmitError={() => setExternalSubmitError(null)}
      contentType={
        isImportMoreDataMode && contentTypeFromParams ? contentTypeFromParams : undefined
      }
    />
  );

  // The import popup is its own modal, so it skips ImportModalShell.
  if (isModal && !schemaConfig && showImportPopup && !isAwaitingSheetChoice) {
    return compactImportContent;
  }

  if (isModal && !schemaConfig) {
    // One persistent modal shell across both steps — switching the children
    // (source selection ↔ add data) keeps oolib's Modal mounted, so its
    // slide-in animation plays once instead of on every view change.
    return (
      <ImportModalShell
        onClose={() => onClose && onClose()}
        // Header back button shows only on the add-data step, and not when we
        // launched straight into an adapter from an Integrations card (no
        // source-selection step to go back to). The sheet step has its own
        // "Go Back" in the body, so it keeps the plain titled header.
        onBack={
          selectedDataSource && !integrationActivation && !isAwaitingSheetChoice
            ? () => setSelectedDataSource(null)
            : undefined
        }
      >
        {compactImportContent}
      </ImportModalShell>
    );
  }

  return (
    <div
      style={{
        ...(isModal
          ? {
              width: "95dvw",
              height: "95dvh",
              overflowY: "hidden",
              borderRadius: "12px",
              border: `1px solid ${colors2.grey10}`,
              boxShadow: `4px 4px 12px 0 ${colors2.grey5}`,
            }
          : {
            minHeight: "100dvh",
          }),
        ...(shouldApplyOverflowHiddenFix && {
          maxHeight: "100dvh",
          overflowY: "hidden",
        }),

        background: colors2.white,
      }}
      ref={ref_modalShell}
    >
      {navigationGuard.showConfirmModal && (
        <ModalConfirm
          title={navigationGuard.message}
          onClose={navigationGuard.onCancel}
          onConfirm={navigationGuard.onConfirm}
          onConfirmText="Leave"
          onCloseText="Stay"
        />
      )}
      {showVerifyMappingModal && (
        <VerifyMappingModal
          outline={schemaConfig?.schemaExtractor?.outline || []}
          options={verifyMappingCompOptions}
          onFieldChange={(index, v) =>
            setSchemaConfig((prev) =>
              applyOutlineFieldChange(prev, index, v, verifyMappingCompOptions)
            )
          }
          onGoBack={() => setShowVerifyMappingModal(false)}
          isFirstImport={!(isImportMoreDataMode && contentTypeFromParams)}
          // On a reimport this is the final step, so it carries the same CTA the
          // first-import flow puts on its last form (the name-the-dataset modal).
          continueText={
            isImportMoreDataMode && contentTypeFromParams
              ? "Begin Import"
              : "Continue"
          }
          onContinue={() => {
            setShowVerifyMappingModal(false);
            // Reimport has no dataset to name — the fields it would ask about
            // already exist — so Continue is the import itself.
            if (isImportMoreDataMode && contentTypeFromParams) {
              submitReimport();
            } else {
              setShowContentTypeTitleModal(true);
            }
          }}
        />
      )}
      {showContentTypeTitleModal && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)" /* Safari support */,
            background: "rgba(210, 210, 210, 0.8)",
            zIndex: 100,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10000,
              background: "white",

              border: `1px solid ${colors2.grey10}`,
              borderRadius: "6px",
              width: "54rem",
              boxShadow: `2px 2px 10px ${colors2.grey5}`,
            }}
          >
            <StyledConfirmModalHeader>
              <UI_BODY_BOLD>Confirm Import</UI_BODY_BOLD>
              <ButtonGhost
                icon="X"
                onClick={() => setShowContentTypeTitleModal(false)}
              />
            </StyledConfirmModalHeader>
            <div style={{ padding: "1.2rem", paddingTop: 0 }}>
              <ContentTypeTitleModal
                {...{
                  setSchemaConfig,
                  schemaConfig,
                  directImportData,
                  setShowContentTypeTitleModal,
                  integrationActivation,
                  isModal,
                  onClose,
                  pendingDirectDataImportId: resumePendingDirectDataImport?._id,
                }}
              />
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {(isImporting || isReImporting) && (
          <LoaderOverlay
            loaderText={SM_UIContent.SM_DirectDataImport.display.importData}
          />
        )}
        {isUploading && (
          <LoaderOverlay
            loaderText={SM_UIContent.SM_DirectDataImport.display.processExcel}
          />
        )}
        {isFetchingExternal && (
          <LoaderOverlay
            loaderText="Fetching data from external source..."
          />
        )}
        <div
          style={{
            padding: `0 ${LAYOUT_CONSTANTS.pagePadding}px`,
            ...(isModal ? {} : { borderBottom: `1px solid ${colors2.grey10}` }),
          }}
        >
          {!isModal && (
            <Breadcrumbs
              links={
                isImportMoreDataMode
                  ? [
                      {
                        display: SM_UIContent.SM_DataImport.display.datasets,
                        to: "/datasets",
                      },
                      {
                        display: tpl?.general?.content?.title,
                        to: `/datasets/${contentTypeFromParams}`,
                      },
                      {
                        display: SM_UIContent.SM_DataImport.display.import,
                      },
                    ]
                  : [
                      {
                        display:
                          SM_UIContent.SM_DirectDataImport.display.datasets,
                        to: "/datasets",
                      },
                      {
                        display:
                          SM_UIContent.SM_DirectDataImport.display
                            .importNewDataset,
                      },
                    ]
              }
            />
          )}
        </div>
        <div
          style={{
            psitoion: "sticky",
            top: 0,
            zIndex: 99,
            backgroundColor: colors2.white,
          }}
        >
          <SM_PageHeader
            title={
              isImportMoreDataMode
                ? `Import CSV in “${tpl?.general?.content?.title || ""}”`
                : SM_UIContent.SM_DirectDataImport.display.importNewDataset
            }
            customLeftComp={
              schemaConfig && previewRowCounts ? (
                <UI_CAPTION color={colors2.grey40}>
                  {`Loaded ${previewRowCounts.loaded} of ${previewRowCounts.total} rows`}
                </UI_CAPTION>
              ) : undefined
            }
            // betaBadge
            // customLeftComp={
            //   //isModal &&
            //   // Hide field-layout controls for integration-driven imports
            //   // (fathom, slack, zohodesk, playstore, etc.) — their schema is
            //   // fixed by the adapter, so these options don't apply.
            //   !integrationActivation && schemaConfig && (
            //     <div style={{ paddingLeft: "1rem" }}>
            //       <div
            //         style={{ display: "flex", gap: 6, alignItems: "center" }}
            //       >
            //         {/* This will be a csv separator selection in the future. */}
            //         {/* <DropdownSingle S={true} />  */}
            //         <DropdownSingle
            //           S={true}
            //           value={selectedFieldLayout}
            //           onChange={(k, v) => setSelectedFieldLayout(v)}
            //           options={fieldLayoutOptions}
            //         />
            //         <div
            //           style={{
            //             border: `0.1rem solid ${colors2.grey10}`,
            //             borderRadius: 6,
            //             padding: "0.8rem 1.2rem",
            //           }}
            //         >
            //           <SwitchSingle
            //             option={{
            //               display: "First row is field title",
            //               value: true,
            //             }}
            //             value={false}
            //           />
            //         </div>
            //       </div>
            //     </div>
            //   )
            // }
            customRightComp={
              // isModal &&
              schemaConfig ? (
                // alignItems: the Lightbox trigger is an inline-flex span, so a
                // stretch row would size View Schema to the tallest sibling
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  <Lightbox placement="bottom-end">
                    <LightboxTrigger>
                      <ButtonSecondaryCompact icon="Eye">
                        View Schema
                      </ButtonSecondaryCompact>
                    </LightboxTrigger>
                    <LightboxContent style={{ width: "41rem", zIndex: 999999999 }}>
                      <div style={{ padding: "0.8rem 1.6rem" }}>
                        <FieldTypeList
                          outline={schemaConfig?.schemaExtractor?.outline || []}
                        />
                      </div>
                    </LightboxContent>
                  </Lightbox>
                  <TooltipV2 primaryContent={importInfoTooltipContent} trigger="click">
                    <ButtonTertiaryV2 icon="Info" />
                  </TooltipV2>
                  {/* Drafts are for NEW dataset imports only. A reimport draft
                      would freeze the outline against a tpl that can change
                      underneath it (e.g. a field type change), and unlike a
                      fresh upload it never gets re-merged against the current
                      tpl on resume — so reimports must always start from an
                      upload, never a saved outline. */}
                  {!isImportMoreDataMode && (
                    <div>
                      <ButtonSecondary
                        onClick={() =>
                          pendingDirectDataImportSession.saveDraft({
                            schemaConfig,
                          })
                        }
                        disabled={pendingDirectDataImportSession.isSaving}
                      >
                        {pendingDirectDataImportSession.isSaving ? "Saving..." : "Save as Draft"}
                      </ButtonSecondary>
                    </div>
                  )}
                    {isModal && (
                     <>
                       {showDiscardModal && (
                         <ModalConfirm
                           title={discardImportMsg}
                           onClose={() => setShowDiscardModal(false)}
                           onConfirm={() => {
                             setShowDiscardModal(false);
                             // Discarding a resumed draft deletes it — the work
                             // it held is exactly what's being discarded.
                             pendingDirectDataImportSession.deleteOnDiscard();
                             // Clearing schemaConfig drops back to the source-selection
                             // step, which is the previous step when importing a NEW
                             // dataset. Importing into an EXISTING one is entered from
                             // that dataset, so there's nothing to go back to — the
                             // file picker isn't a step the user chose to be on, and
                             // re-showing it reads as the discard having failed. Reset
                             // either way (a reopened modal must start clean), then
                             // close outright in the existing-dataset flow.
                             setSchemaConfig(undefined);
                             setHeaderValidationErrors({});
                             setShouldValidateOnBlur(false);
                             if (isImportMoreDataMode) onClose && onClose();
                           }}
                           onConfirmText="Discard"
                           onCloseText="Cancel"
                         />
                       )}
                       <ButtonSecondary
                         icon="Trash"
                         onClick={() => setShowDiscardModal(true)}
                       >
                         Discard Import
                       </ButtonSecondary>
                     </>
                    )}
                  <div style={{ position: "relative" }}>
                    <ButtonPrimary
                      icon="Check"
                      // Guard against double-submit across BOTH reimport phases
                      // (type change, then import) and the first-import path.
                      disabled={
                        isImporting || isReImporting || isChangingFieldComp
                      }
                      onClick={() => {
                        // Enable validation on blur from now on
                        setShouldValidateOnBlur(true);

                        // Trigger validation to show errors in header cells:
                        // field-name errors + reimport "map to existing field" errors.
                        const errors = {
                          ...validateAllHeaderCells(
                            schemaConfig.schemaExtractor.outline
                          ),
                          ...validateMappings(
                            schemaConfig.schemaExtractor.outline
                          ),
                        };
                        if (Object.keys(errors).length > 0) {
                          // Update state to show errors in UI
                          setHeaderValidationErrors(errors);

                          // Scroll to first error header cell
                          setTimeout(() => {
                            const firstErrorColId = Object.keys(errors)[0];
                            const firstErrorCellIndex =
                              schemaConfig.schemaExtractor.outline.findIndex(
                                (field) =>
                                  field.targetValuePath === firstErrorColId
                              );

                            if (firstErrorCellIndex !== -1) {
                              const headerCells =
                                document.querySelectorAll("#TableHeaderCell");
                              const errorCell =
                                headerCells[firstErrorCellIndex];

                              if (errorCell) {
                                errorCell.scrollIntoView({
                                  behavior: "smooth",
                                  block: "nearest",
                                  inline: "center",
                                });
                              }
                            }
                          }, 100);

                          return;
                        }

                        // Both paths review the field mapping before anything
                        // is written; where Continue leads differs (see the
                        // modal's onContinue).
                        setShowVerifyMappingModal(true);
                      }}
                    >
                      Confirm Import
                    </ButtonPrimary>
                  </div>
                </div>
              ) : (
                isModal && (
                  <div style={{ display: "flex", gap: "6px" }}>
                    <ButtonSecondary
                      icon="X"
                      onClick={() => onClose && onClose()}
                    >
                      Cancel Import
                    </ButtonSecondary>
                  </div>
                )
              )
            }
          />
        </div>

        {schemaConfig ? (
          <>
            <PreviewLayout
              schemaConfig={schemaConfig}
              setSchemaConfig={setSchemaConfig}
              ref_modalShell={ref_modalShell}
              headerValidationErrors={headerValidationErrors}
              setHeaderValidationErrors={setHeaderValidationErrors}
              shouldValidateOnBlur={shouldValidateOnBlur}
              isModal={isModal}
              isImportMoreDataMode={isImportMoreDataMode}
              onRowCountsChange={setPreviewRowCounts}
            />
            {showGettingStarted && (
              <GettingStartedModal
                outline={schemaConfig.schemaExtractor.outline}
                onContinue={() => setShowGettingStarted(false)}
              />
            )}
            {/* <div
              style={{
                position: "sticky",
                bottom: isModal ? "0" : "1.1rem",
                width: "100%",
                zIndex: 2,
              }}
            >
              <ResizablePanel defaultHeight={50} minHeight={10} maxHeight={80}>
                <PublishedPreviewPageLayout
                  schemaConfig={schemaConfig}
                  disableStickyContentHeader={true}
                />
              </ResizablePanel>
            </div> */}
          </>
        ) : (
          // In the modal, the shell is a fixed 95dvh with overflow hidden, so
          // tall interfaces (e.g. Play Store reviews with several dropdowns)
          // clip the Fetch button. Make this branch a scrollable flex child so
          // the body scrolls instead. Scoped to isModal — the full-page view
          // scrolls naturally via the document and shouldn't get an inner box.
          <div
            style={
              isModal
                ? { flex: "1 1 auto", minHeight: 0, overflowY: "auto" }
                : undefined
            }
          >
          <Wrapper800>
            <div style={{ padding: LAYOUT_CONSTANTS.pagePadding + "px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: LAYOUT_CONSTANTS.topBottomPadding + "px",
                }}
              >
                <SystemComsTextBox>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <UI_BODY_SEMIBOLD_SM_DF>
                      This feature is in Beta. It’s designed to help you get
                      started quickly if you already have a clean dataset
                      without too many complexities.
                    </UI_BODY_SEMIBOLD_SM_DF>
                    <UI_BODY_SEMIBOLD_SM_DF>
                      We’re rolling out several updates in the coming weeks —
                      stay tuned.
                    </UI_BODY_SEMIBOLD_SM_DF>
                  </div>
                </SystemComsTextBox>

                {/* Source selection + add data — same compact content as the
                    modal, rendered inline here (no modal) for full-page routes. */}
                {selectedDataSource && !integrationActivation && (
                  <div style={{ marginBottom: "12px" }}>
                    <ButtonGhost
                      icon="ArrowLeft"
                      onClick={() => setSelectedDataSource(null)}
                    >
                      Back to source selection
                    </ButtonGhost>
                  </div>
                )}
                {compactImportContent}
              </div>
            </div>
          </Wrapper800>
          </div>
        )}
        <SavePendingDirectDataImportModal
          isOpen={pendingDirectDataImportSession.showSaveModal}
          onClose={() => pendingDirectDataImportSession.setShowSaveModal(false)}
          schemaExtractorOutline={schemaConfig?.schemaExtractor?.outline}
          fileNameInGcs={schemaConfig?.fileNameInGcs}
          kp_content_type={contentTypeFromParams}
          // A draft keeps which integration its rows came from (and the
          // adapter options), so resuming it still links the dataset — see
          // buildSchemaConfigFromPendingDirectDataImport.
          metadata={
            schemaConfig?.integrationProvider
              ? {
                  ...schemaConfig?.metadata,
                  integration: {
                    provider: schemaConfig.integrationProvider,
                    adapterOptions: schemaConfig?.adapterOptions || {},
                  },
                }
              : schemaConfig?.metadata
          }
          onSaveSuccess={resetImportSession}
        />
      </div>
    </div>
  );
};
